import { Show } from '@clerk/react'
import { ArrowRight, BookOpen, CheckCircle2, Code2, Trophy } from 'lucide-react'
import { Link } from '#router'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { useIsMobile } from '@/hooks/use-mobile'
import { MARKETING_PAGES_FLAG } from '@/lib/marketing-content'
import Footer from './footer'
import { MobileHome } from './mobile-home'

export default function Home() {
  const marketingFlag = useFeatureFlag(MARKETING_PAGES_FLAG)
  const isMobile = useIsMobile()
  if (isMobile) return <MobileHome />
  return (
    <div className="flex flex-col">
      <div className="relative overflow-hidden">
        <div className="container relative mx-auto px-4 py-8 md:py-16">
          {/* Platform Hero Section */}
          <div className="mx-auto mb-8 flex max-w-4xl flex-col items-center space-y-4 text-center">
            <h1 className="text-4xl leading-tight font-bold tracking-tight md:text-6xl">
              <span className="block">Master Interviews</span>
              <span className="block">with</span>
              <span className="text-[#51B0BC] block">Beyond CTCI</span>
            </h1>
            <p className="text-muted-foreground max-w-3xl text-lg md:text-xl">
              A modern companion platform for{' '}
              <em>Beyond Cracking the Coding Interview</em>. Write, run, and
              test your solutions to every problem so you&rsquo;re prepared for
              the same bar that top candidates are held to.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 pt-1">
              <Button asChild size="lg" className="group text-base">
                <Link href="/problems">
                  Start Practicing
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-base">
                <a
                  href="https://www.amazon.com/dp/195570600X"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Buy the Book
                </a>
              </Button>
              {marketingFlag.isLoaded && marketingFlag.enabled && (
                <Show when="signed-out">
                  <Button
                    asChild
                    size="lg"
                    variant="ghost"
                    className="text-base"
                  >
                    <Link href="/how-it-works">How the Program Works</Link>
                  </Button>
                </Show>
              )}
            </div>
          </div>

          {/* Stats Strip */}
          <div className="mb-8 grid gap-4 sm:grid-cols-3">
            <div className="bg-card rounded-xl border px-5 py-4 text-center">
              <p className="text-[#51B0BC] text-2xl font-semibold">26</p>
              <p className="text-muted-foreground text-sm">Technical topics</p>
            </div>
            <div className="bg-card rounded-xl border px-5 py-4 text-center">
              <p className="text-[#51B0BC] text-2xl font-semibold">~300</p>
              <p className="text-muted-foreground text-sm">
                Curated interview problems
              </p>
            </div>
            <div className="bg-card rounded-xl border px-5 py-4 text-center">
              <p className="text-[#51B0BC] text-2xl font-semibold">#1</p>
              <p className="text-muted-foreground text-sm">
                Amazon Best Seller in DS&amp;A
              </p>
            </div>
          </div>

          {/* Features Section */}
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
            <Card className="hover:border-primary/40 hover:shadow-primary/10 transition-all hover:-translate-y-1 hover:shadow-lg">
              <CardHeader>
                <div className="bg-primary/10 mb-4 flex h-12 w-12 items-center justify-center rounded-lg">
                  <BookOpen className="text-primary h-6 w-6" />
                </div>
                <CardTitle>Structured Curriculum</CardTitle>
                <CardDescription>
                  Step-by-step editorials covering data structures, algorithms,
                  and problem-solving strategies.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="hover:border-primary/40 hover:shadow-primary/10 transition-all hover:-translate-y-1 hover:shadow-lg">
              <CardHeader>
                <div className="bg-primary/10 mb-4 flex h-12 w-12 items-center justify-center rounded-lg">
                  <Code2 className="text-primary h-6 w-6" />
                </div>
                <CardTitle>Interview-Focused</CardTitle>
                <CardDescription>
                  Purpose-built to complement the book with practical examples
                  and coding exercises.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="hover:border-primary/40 hover:shadow-primary/10 transition-all hover:-translate-y-1 hover:shadow-lg">
              <CardHeader>
                <div className="bg-primary/10 mb-4 flex h-12 w-12 items-center justify-center rounded-lg">
                  <Trophy className="text-primary h-6 w-6" />
                </div>
                <CardTitle>Author-Maintained</CardTitle>
                <CardDescription>
                  Beyond CTCI content assembled by the authors and continuously
                  updated for current interview trends.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </div>

      {/* Book Hero Section */}
      <div className="bg-muted/40 border-y">
        <div className="container mx-auto grid items-center gap-8 px-4 py-12 md:grid-cols-2 md:gap-12 md:py-20">
          <div className="flex flex-col space-y-6 text-center md:text-left">
            <p className="text-primary text-sm font-semibold tracking-wide uppercase">
              Official Sequel
            </p>
            <h2 className="text-4xl font-bold tracking-tight md:text-5xl">
              The Sequel to the Best-Selling Interview Classic
            </h2>
            <div className="text-muted-foreground space-y-4 text-lg">
              <p>
                For over a decade,{' '}
                <em className="text-[#51B0BC]">
                  Cracking the Coding Interview
                </em>{' '}
                has been hailed as the &ldquo;bible&rdquo; of interview prep.
                Now, Beyond Cracking the Coding Interview builds on that
                foundation to prepare you for today&rsquo;s tougher technical
                interviews and hiring climate.
              </p>
            </div>
            <div className="space-y-3">
              <div className="text-foreground flex items-start justify-center gap-2 text-left md:justify-start">
                <CheckCircle2 className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                <span>
                  26 technical topics and ~300 curated coding problems
                </span>
              </div>
              <div className="text-foreground flex items-start justify-center gap-2 text-left md:justify-start">
                <CheckCircle2 className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                <span>
                  Also use{' '}
                  <a
                    href="https://start.interviewing.io/beyond-ctci/all-problems/technical-topics"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600/90 font-medium underline decoration-sky-500/40 underline-offset-4 transition-colors hover:text-sky-500 hover:decoration-sky-500"
                  >
                    interviewing.io&apos;s free AI interviewer companion
                  </a>{' '}
                  for guided practice
                </span>
              </div>
              <div className="text-foreground flex items-start justify-center gap-2 text-left md:justify-start">
                <CheckCircle2 className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                <span>Insights from 100k+ interviews and hiring data</span>
              </div>
            </div>
            <div className="pt-2">
              <Button asChild size="lg" className="text-base">
                <a
                  href="https://www.amazon.com/dp/195570600X"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Buy Now
                </a>
              </Button>
            </div>
          </div>
          <div className="flex justify-center">
            <div className="from-primary/25 to-primary/5 rounded-3xl bg-gradient-to-br p-5 shadow-2xl ring-1 ring-white/20">
              <img
                src="/images/book-cover.png"
                alt="Beyond Cracking the Coding Interview book cover"
                width={540}
                height={675}
                className="w-full max-w-sm rounded-xl shadow-2xl transition-transform duration-300 hover:scale-[1.02]"
              />
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}
